import {
  parseCanvasPackage,
  type CanvasPackageParseResult,
} from '@littlebird/canvas';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { YStack } from '@/components/ui';
import { Iframe } from '@/components/ui/Iframe';
import { useSpriteExternalLinks } from '@/features/sprites/use-sprite-external-links';
import { cn } from '@/lib/utils';
import { useTheme } from '@/providers/ThemeProvider';

import {
  buildCanvasDocument,
  CANVAS_DOCUMENT_HEIGHT_MESSAGE,
  CANVAS_DOCUMENT_MEASURE_MESSAGE,
  CANVAS_DOCUMENT_WHEEL_MESSAGE,
} from './canvas-document';
import { type CanvasHostTheme, readCanvasHostTheme } from './canvas-theme';
import {
  CanvasActionCardFallback,
  CanvasActionCardOverlay,
} from './CanvasActionCardOverlay';
import { useCanvasActionCards } from './use-canvas-action-cards';
import { useCanvasChatPrompt } from './use-canvas-chat-prompt';

const MAX_CANVAS_DOCUMENT_HEIGHT = 100_000;
const MAX_CANVAS_WHEEL_DELTA = 1_000;
const CANVAS_MEASUREMENT_CACHE_LIMIT = 20;

type CachedCanvasMeasurement = {
  canvasFingerprint: string;
  height: number;
  width: number;
};

const canvasMeasurementByKey = new Map<string, CachedCanvasMeasurement>();

const getCachedCanvasMeasurement = (cacheKey: string) => {
  return canvasMeasurementByKey.get(cacheKey);
};

const promoteCanvasMeasurement = (
  cacheKey: string,
  measurement: CachedCanvasMeasurement
) => {
  if (canvasMeasurementByKey.get(cacheKey) !== measurement) return;

  canvasMeasurementByKey.delete(cacheKey);
  canvasMeasurementByKey.set(cacheKey, measurement);
};

const rememberCanvasMeasurement = (
  cacheKey: string,
  measurement: CachedCanvasMeasurement
) => {
  canvasMeasurementByKey.delete(cacheKey);
  canvasMeasurementByKey.set(cacheKey, measurement);

  if (canvasMeasurementByKey.size > CANVAS_MEASUREMENT_CACHE_LIMIT) {
    const oldestCacheKey = canvasMeasurementByKey.keys().next().value;
    if (oldestCacheKey) canvasMeasurementByKey.delete(oldestCacheKey);
  }
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const fingerprintCanvasText = (canvasText: string) => {
  let hash = 2_166_136_261;
  for (let index = 0; index < canvasText.length; index += 1) {
    hash ^= canvasText.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return `${canvasText.length}:${(hash >>> 0).toString(36)}`;
};

const createCanvasBridgeCapability = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('');

type CanvasFrameProps = {
  canvasText: string;
  title: string;
  chatId?: string;
  cacheKey?: string;
  parseResult?: CanvasPackageParseResult;
  className?: string;
  documentMode?: boolean;
  onDocumentReady?: () => void;
  onDocumentReadyChange?: (isReady: boolean) => void;
};

export const CanvasFrame = ({
  canvasText,
  title,
  chatId,
  cacheKey,
  parseResult: providedParseResult,
  className,
  documentMode = false,
  onDocumentReady,
  onDocumentReadyChange,
}: CanvasFrameProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const frameSurfaceRef = useRef<HTMLDivElement>(null);
  const [documentMeasurement, setDocumentMeasurement] = useState<{
    srcDoc: string;
    height: number;
    width: number;
  } | null>(null);
  const [documentWidth, setDocumentWidth] = useState<number | null>(null);
  const wheelAnimationFrameRef = useRef<number | null>(null);
  const pendingWheelDeltaRef = useRef({ left: 0, top: 0 });
  const readyDocumentKeyRef = useRef<string | null>(null);
  const { theme } = useTheme();
  const [hostTheme, setHostTheme] = useState<CanvasHostTheme>(() =>
    readCanvasHostTheme(theme)
  );
  const initialHostTheme = useRef(hostTheme).current;
  const parseResult = useMemo(
    () => providedParseResult ?? parseCanvasPackage(canvasText),
    [providedParseResult, canvasText]
  );
  const actionCardReferences = useMemo(
    () =>
      parseResult.success ? (parseResult.canvasPackage.actionCards ?? []) : [],
    [parseResult]
  );
  const actionCardsEnabled = !!chatId && actionCardReferences.length > 0;
  const actionCardReferenceKey = actionCardReferences
    .map(({ localId, placement }) => `${localId}:${placement}`)
    .join(',');
  const bridgeCapability = useMemo(createCanvasBridgeCapability, [
    actionCardReferenceKey,
    cacheKey,
    canvasText,
    documentMode,
  ]);
  const canvasFingerprint = useMemo(
    () => fingerprintCanvasText(canvasText),
    [canvasText]
  );
  const srcDoc = useMemo(
    () =>
      parseResult.success
        ? buildCanvasDocument(
            parseResult.canvasPackage,
            initialHostTheme,
            documentMode
              ? {
                  documentMode: true,
                  bridgeCapability,
                  chatPromptCapability: bridgeCapability,
                  ...(actionCardsEnabled
                    ? { actionCardCapability: bridgeCapability }
                    : {}),
                  renderContext: 'embedded',
                }
              : {
                  chatPromptCapability: bridgeCapability,
                  ...(actionCardsEnabled
                    ? { actionCardCapability: bridgeCapability }
                    : {}),
                  renderContext: 'embedded',
                }
          )
        : '',
    [
      actionCardsEnabled,
      bridgeCapability,
      documentMode,
      initialHostTheme,
      parseResult,
    ]
  );
  const cachedDocumentMeasurement = cacheKey
    ? getCachedCanvasMeasurement(cacheKey)
    : undefined;
  useEffect(() => {
    if (!cacheKey || !cachedDocumentMeasurement) return;
    promoteCanvasMeasurement(cacheKey, cachedDocumentMeasurement);
  }, [cacheKey, cachedDocumentMeasurement]);
  const documentHeight =
    documentMeasurement?.srcDoc === srcDoc
      ? documentMeasurement.height
      : cachedDocumentMeasurement?.canvasFingerprint === canvasFingerprint &&
          cachedDocumentMeasurement.width === documentWidth
        ? cachedDocumentMeasurement.height
        : null;
  const readyDocumentKey = !parseResult.success
    ? `invalid:${canvasFingerprint}`
    : documentMode && documentHeight !== null
      ? srcDoc
      : null;
  useSpriteExternalLinks(iframeRef);
  useCanvasChatPrompt(iframeRef, bridgeCapability);
  const {
    slots: actionCardSlots,
    fallbackLocalIds: fallbackActionCardLocalIds,
    handleHeightChange: handleActionCardHeightChange,
  } = useCanvasActionCards({
    iframeRef,
    bridgeCapability,
    enabled: actionCardsEnabled,
    references: actionCardReferences,
  });

  useLayoutEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      setHostTheme(readCanvasHostTheme(theme));
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [theme]);

  useLayoutEffect(() => {
    const iframe = iframeRef.current;
    if (!documentMode || !iframe) {
      setDocumentWidth(null);
      return;
    }

    const updateDocumentWidth = () => {
      const width = iframe.clientWidth;
      setDocumentWidth((currentWidth) =>
        currentWidth === width ? currentWidth : width
      );
    };
    updateDocumentWidth();

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(() => {
      updateDocumentWidth();
      iframe.contentWindow?.postMessage(
        { type: CANVAS_DOCUMENT_MEASURE_MESSAGE },
        '*'
      );
    });
    resizeObserver.observe(iframe);
    return () => resizeObserver.disconnect();
  }, [documentMode, srcDoc]);

  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'littlebird-canvas-theme', theme: hostTheme },
      '*'
    );
  }, [hostTheme]);

  useLayoutEffect(() => {
    onDocumentReadyChange?.(readyDocumentKey !== null);
  }, [onDocumentReadyChange, readyDocumentKey]);

  useLayoutEffect(() => {
    if (
      !onDocumentReady ||
      !readyDocumentKey ||
      readyDocumentKeyRef.current === readyDocumentKey
    ) {
      return;
    }

    readyDocumentKeyRef.current = readyDocumentKey;
    onDocumentReady();
  }, [onDocumentReady, readyDocumentKey]);

  useLayoutEffect(() => {
    if (!documentMode) {
      setDocumentMeasurement(null);
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) return;

      const data = event.data as Record<string, unknown> | null;
      if (!data || data.capability !== bridgeCapability) return;

      if (
        data.type === CANVAS_DOCUMENT_HEIGHT_MESSAGE &&
        typeof data.height === 'number' &&
        Number.isFinite(data.height) &&
        data.height > 0
      ) {
        const height = Math.ceil(
          clamp(data.height, 1, MAX_CANVAS_DOCUMENT_HEIGHT)
        );
        const width = iframe.clientWidth;
        setDocumentWidth(width);
        setDocumentMeasurement({
          srcDoc,
          height,
          width,
        });
        if (cacheKey) {
          rememberCanvasMeasurement(cacheKey, {
            canvasFingerprint,
            height,
            width,
          });
        }
        return;
      }

      if (
        data.type === CANVAS_DOCUMENT_WHEEL_MESSAGE &&
        typeof data.deltaX === 'number' &&
        Number.isFinite(data.deltaX) &&
        typeof data.deltaY === 'number' &&
        Number.isFinite(data.deltaY) &&
        typeof data.deltaMode === 'number' &&
        (data.deltaMode === WheelEvent.DOM_DELTA_PIXEL ||
          data.deltaMode === WheelEvent.DOM_DELTA_LINE ||
          data.deltaMode === WheelEvent.DOM_DELTA_PAGE)
      ) {
        const scrollContainer = iframe.closest<HTMLElement>('.overflow-y-auto');
        if (!scrollContainer) return;

        const unit =
          data.deltaMode === WheelEvent.DOM_DELTA_LINE
            ? 16
            : data.deltaMode === WheelEvent.DOM_DELTA_PAGE
              ? scrollContainer.clientHeight
              : 1;
        const pendingDelta = pendingWheelDeltaRef.current;
        pendingDelta.left = clamp(
          pendingDelta.left + data.deltaX * unit,
          -MAX_CANVAS_WHEEL_DELTA,
          MAX_CANVAS_WHEEL_DELTA
        );
        pendingDelta.top = clamp(
          pendingDelta.top + data.deltaY * unit,
          -MAX_CANVAS_WHEEL_DELTA,
          MAX_CANVAS_WHEEL_DELTA
        );

        if (wheelAnimationFrameRef.current === null) {
          wheelAnimationFrameRef.current = requestAnimationFrame(() => {
            const { left, top } = pendingWheelDeltaRef.current;
            pendingWheelDeltaRef.current = { left: 0, top: 0 };
            wheelAnimationFrameRef.current = null;
            scrollContainer.scrollBy({ left, top });
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      if (wheelAnimationFrameRef.current !== null) {
        cancelAnimationFrame(wheelAnimationFrameRef.current);
        wheelAnimationFrameRef.current = null;
      }
      pendingWheelDeltaRef.current = { left: 0, top: 0 };
    };
  }, [bridgeCapability, cacheKey, canvasFingerprint, documentMode, srcDoc]);

  if (!parseResult.success) {
    return (
      <YStack className="h-full items-center justify-center gap-2 p-6 text-center">
        <span className="text-sm font-medium text-foreground">
          This canvas could not be opened
        </span>
        <span className="text-xs text-foreground-muted">
          {parseResult.error}
        </span>
      </YStack>
    );
  }

  const frame = (
    <Iframe
      ref={iframeRef}
      title={title}
      srcDoc={srcDoc}
      onLoad={() => {
        const contentWindow = iframeRef.current?.contentWindow;
        contentWindow?.postMessage(
          { type: 'littlebird-canvas-theme', theme: hostTheme },
          '*'
        );
        if (documentMode) {
          contentWindow?.postMessage(
            { type: CANVAS_DOCUMENT_MEASURE_MESSAGE },
            '*'
          );
        }
      }}
      sandbox="allow-scripts"
      className={
        actionCardsEnabled ? 'absolute inset-0 size-full border-0' : className
      }
      scrolling={documentMode ? 'no' : undefined}
      tabIndex={documentMode && documentHeight === null ? -1 : undefined}
      style={
        documentMode
          ? {
              height: documentHeight ?? 1,
              opacity: documentHeight === null ? 0 : 1,
              backgroundColor: 'var(--background-subtle)',
            }
          : undefined
      }
    />
  );

  if (!actionCardsEnabled || !chatId) return frame;

  const frameHeight = documentMode
    ? (documentHeight ?? 1)
    : (frameSurfaceRef.current?.clientHeight ?? 0);
  const frameWidth = frameSurfaceRef.current?.clientWidth ?? 0;

  return (
    <div
      className={cn(
        'relative overflow-hidden',
        !documentMode && 'flex flex-col',
        className
      )}
      style={
        documentMode
          ? {
              opacity: documentHeight === null ? 0 : 1,
              backgroundColor: 'var(--background-subtle)',
            }
          : undefined
      }
    >
      <div
        ref={frameSurfaceRef}
        className={cn(
          'relative w-full overflow-hidden',
          documentMode ? 'shrink-0' : 'min-h-0 flex-1'
        )}
        style={documentMode ? { height: documentHeight ?? 1 } : undefined}
      >
        {frame}
        <CanvasActionCardOverlay
          chatId={chatId}
          slots={actionCardSlots}
          frameWidth={frameWidth}
          frameHeight={frameHeight}
          onHeightChange={handleActionCardHeightChange}
        />
      </div>
      <CanvasActionCardFallback
        chatId={chatId}
        localIds={fallbackActionCardLocalIds}
      />
    </div>
  );
};
